import { computed } from "vue";
import { createJsonStorageAdapter, createLanguageController, createSettingsStore, createThemeController } from "@lunagoai/vue-app-core";
import type { AdminSettings, AdminUiPreferences, AdminLanguage, AdminTheme } from "@shared/admin";

export const ADMIN_DEFAULT_UI_PREFERENCES: AdminUiPreferences = {
  language: "en",
  theme: "system",
};

export const ADMIN_DEFAULT_SERVER_SETTINGS: AdminSettings = {
  paperWidth: 80,
  printCopies: "kitchen",
  autoSync: true,
  syncIntervalSeconds: 60,
  updatedAt: "",
};

export function createAdminAppCore() {
  const uiPreferences = createSettingsStore<AdminUiPreferences>({
    defaults: ADMIN_DEFAULT_UI_PREFERENCES,
    storage: createJsonStorageAdapter<AdminUiPreferences>({ key: "posnext.admin-ui-preferences" }),
  });
  const serverSettings = createSettingsStore<AdminSettings>({ defaults: ADMIN_DEFAULT_SERVER_SETTINGS });
  const themeValue = computed<AdminTheme>({
    get: () => uiPreferences.settings.value.theme,
    set: (value) => uiPreferences.set("theme", value),
  });
  const languageValue = computed<AdminLanguage>({
    get: () => uiPreferences.settings.value.language,
    set: (value) => uiPreferences.set("language", value),
  });
  // vue-ui's canonical dark-mode contract is the root `.dark` class. Keep
  // the app-core controller as the single writer for that state.
  const theme = createThemeController({ theme: themeValue, darkClass: "dark" });
  const language = createLanguageController({
    currentLanguage: languageValue,
    availableLanguages: ["en", "zh-Hant", "zh-Hans"] as const,
    setLanguage: (value) => uiPreferences.set("language", value),
  });

  return {
    uiPreferences,
    serverSettings,
    theme,
    language,
    applyServerSettings(remote: AdminSettings): void {
      serverSettings.patch(remote);
    },
  };
}
